import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Card, Flex, Button, Modal, Select, message } from "antd"
import { ShareAltOutlined, EnvironmentOutlined, SyncOutlined } from "@ant-design/icons"
import WorkflowCard from "./WorkflowCard"
import { sendNewStoreLocationsEmail, getSingleMasterData, createDatabase } from "../../services/adminServices"
import { submitRequestForm, updateRequestMaster } from "../../services/requestServices"
import { useTranslation } from 'react-i18next';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Pull a value out of a backend location record by matching its key labels.
const pickByLabel = (record, patterns) => {
    const key = Object.keys(record || {}).find(k => patterns.some(pattern => pattern.test(String(k).toLowerCase())))
    return key ? String(record[key] ?? "").trim() : ""
}

// Map a backend `newLocations` record (keyed by the master's field labels,
// e.g. "Brand Name" / "City Name" / "Store Address") to a display shape.
const parseNewLocation = (record) => {
    const values = Object.values(record || {}).map(value => String(value ?? "").trim()).filter(Boolean)
    const name = pickByLabel(record, [/brand/, /store\s*name/, /^name$/]) || values[0] || ""
    const city = pickByLabel(record, [/city/, /town/])
    const address = pickByLabel(record, [/address/, /location/]) || values.filter(value => value !== name).join(", ")
    return { name, city, address, line: values.join(" - ") }
}

const GooglePlacesResultsCard = ({ locations, requestId, masterId, onPersistLocations, updatedInMaster, onRefreshRequest }) => {
    const { t } = useTranslation();
    const [shareModalOpen, setShareModalOpen] = useState(false)
    const [recipients, setRecipients] = useState([])
    const [sharing, setSharing] = useState(false)
    const [updatingMaster, setUpdatingMaster] = useState(false)
    // Hide the Update button as soon as the call succeeds, ahead of the request
    // reload confirming it via `updatedInMaster` (which drives the hide afterwards).
    const [masterJustUpdated, setMasterJustUpdated] = useState(false)

    // Only render for requests that carry the new-store-locations feature.
    if (!Array.isArray(locations)) return null

    const stores = locations.map(parseNewLocation).filter(store => store.name || store.address)
    const showUpdateButton = !updatedInMaster && !masterJustUpdated

    const handleCancelShare = () => setShareModalOpen(false)

    const handleUpdateMaster = async () => {
        if (!requestId) {
            message.error(t('Missing request reference. Cannot update master.'))
            return
        }
        setUpdatingMaster(true)
        try {
            const response = await updateRequestMaster(requestId)
            if (response.status !== 200) {
                message.error(response.message || "Failed to update master")
                return
            }
            message.success(t('Master updated successfully'))
            setMasterJustUpdated(true)
            if (typeof onRefreshRequest === "function") {
                await onRefreshRequest()
            }
        } finally {
            setUpdatingMaster(false)
        }
    }

    // Append the shared store locations to the master table configured on the
    // Google Places node (`masterToBeUpdated`). Runs after the share email sends.
    const updateMasterWithNewLocations = async () => {
        if (!masterId || locations.length === 0) return
        const masterResponse = await getSingleMasterData(masterId)
        const master = masterResponse?.data
        if (!master?.dataId) {
            message.warning(t('Store locations shared, but the master table could not be loaded.'))
            return
        }
        // Align each backend record's keys to the master's actual field labels.
        const fields = master.fields || []
        const data = locations.map(record => {
            const row = {}
            fields.forEach(field => {
                const key = Object.keys(record || {}).find(k => String(k).trim().toLowerCase() === String(field.label).trim().toLowerCase())
                row[field.label] = key ? record[key] : ""
            })
            return row
        })
        const saveResponse = await createDatabase({
            dataId: master.dataId,
            dataName: master.dataName,
            fields: master.fields,
            data,
        }, true)
        if (saveResponse.status !== 200) {
            message.warning(saveResponse.message || "Store locations shared, but the master update failed.")
        }
    }

    const handleShare = async () => {
        const emails = recipients.map(email => String(email || "").trim()).filter(Boolean)
        if (emails.length === 0) {
            message.warning(t('Add at least one email address'))
            return
        }
        const invalid = emails.filter(email => !EMAIL_REGEX.test(email))
        if (invalid.length > 0) {
            message.error(`Invalid email address: ${invalid.join(", ")}`)
            return
        }
        if (!requestId) {
            message.error(t('Missing request reference. Cannot share store locations.'))
            return
        }

        setSharing(true)
        try {
            const response = await sendNewStoreLocationsEmail(requestId, emails)
            if (response.status !== 200) {
                message.error(response.message || "Failed to share store locations")
                return
            }
            // After the mail is sent, add the new locations to the master.
            await updateMasterWithNewLocations()
            // Freeze the found locations onto the request so they survive the backend
            // recompute (which turns newLocations empty once they exist in the master).
            if (typeof onPersistLocations === "function") {
                await onPersistLocations(locations)
            }
            message.success(`Store locations shared with ${emails.length} recipient${emails.length > 1 ? "s" : ""}`)
            setShareModalOpen(false)
            setRecipients([])
        } finally {
            setSharing(false)
        }
    }

    return (
        <>
            <Card
                className="google-places-results-card"
                title={
                    <Flex className="card-title google-places-results-title" align="center" gap={8}>
                        <div className="card-label" style={{ background: "#E7F5FF" }}>
                            <EnvironmentOutlined style={{ color: "#141BCB" }} />
                        </div>
                        <span>{t('New Store Locations')}</span>
                        <Button
                            className="default-btn google-places-share-btn"
                            icon={<ShareAltOutlined />}
                            style={{ marginLeft: "auto" }}
                            onClick={() => setShareModalOpen(true)}
                        >
                            {t('Share')}
                        </Button>
                        {showUpdateButton && (
                            <Button
                                className="default-btn google-places-update-btn"
                                icon={<SyncOutlined />}
                                onClick={handleUpdateMaster}
                                loading={updatingMaster}
                            >
                                {t('Update')}
                            </Button>
                        )}
                        <span className="google-places-results-count" style={{ marginLeft: 0 }}>{stores.length}</span>
                    </Flex>
                }
            >
                <div className="google-places-results-body">
                    {stores.length === 0 ? (
                        <div style={{ padding: 16, color: "#667085", fontSize: 13 }}>
                            {t('No new stores found')}
                        </div>
                    ) : (
                        <ol className="google-places-results-list">
                            {stores.map((store, index) => (
                                <li key={`${store.line}-${index}`} className="google-places-result-item">
                                    <span className="google-places-result-index">{index + 1}</span>
                                    <div className="google-places-result-copy">
                                        <div className="google-places-result-name">{store.name}</div>
                                        {store.address ? <div className="google-places-result-address">{store.address}</div> : null}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </Card>
            <Modal
                title={
                    <Flex align="center" gap={8}>
                        <ShareAltOutlined />
                        <span>{t('Share store locations')}</span>
                    </Flex>
                }
                open={shareModalOpen}
                onCancel={handleCancelShare}
                footer={null}
                destroyOnHidden
            >
                <div style={{ marginBottom: 8, color: "#475467", fontSize: 13 }}>
                    {t('Enter one or more email addresses. Press Enter or comma to add each recipient.')}
                </div>
                <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="name@example.com"
                    value={recipients}
                    onChange={setRecipients}
                    tokenSeparators={[",", " ", ";"]}
                    open={false}
                    suffixIcon={null}
                />
                <Flex justify="flex-end" gap={8} style={{ marginTop: 20 }}>
                    <Button onClick={handleCancelShare} disabled={sharing}>{t('Cancel')}</Button>
                    <Button type="primary" className="submit-button" icon={<ShareAltOutlined />} onClick={handleShare} loading={sharing}>{t('Share')}</Button>
                </Flex>
            </Modal>
        </>
    )
}

const Workflow = (props) => {
    const { t } = useTranslation();
    const { workflowData, requestId, requestDetail, defaultRequestDetail, currentValues, hideGooglePlacesResults, getRequestDetail } = props
    const [workflow, setWorkflow] = useState([])
    const { spaceSpecificUserPickers = [], spaceSpecificUsers=[], spaceSpecificGroups=[] } = useSelector((state) => state.userRoleGroupState)
    let approvedStatusLabel = defaultRequestDetail?.originalWorkflow?.nodes?.find(node => node.data?.type === "end")?.data?.name || ""
    const activeRequestId = requestId !== "new"
        ? requestDetail?.requestId || defaultRequestDetail?.requestId || requestId
        : ""
    // New store locations are computed and returned by the backend on the request.
    // Once shared, they are snapshotted onto the request (`sharedStoreLocations`); prefer
    // that snapshot so the list is not reset when the backend recomputes newLocations as
    // empty after the stores have been added to the master.
    const persistedLocations = [defaultRequestDetail?.sharedStoreLocations, requestDetail?.sharedStoreLocations].find(Array.isArray)
    const computedLocations = [defaultRequestDetail?.newLocations, requestDetail?.newLocations].find(Array.isArray)
    // Set by the backend once `request/update-master` has run for this request.
    const updatedInMaster = Boolean(requestDetail?.updatedInMaster || defaultRequestDetail?.updatedInMaster)
    const newLocations = activeRequestId && !hideGooglePlacesResults
        ? ((persistedLocations && persistedLocations.length > 0) ? persistedLocations : computedLocations)
        : undefined

    // Persist the found store locations onto the request document so they are not lost
    // when the backend recomputes newLocations (empty) after the master gains these stores.
    const persistLocationsOnRequest = async (locationsToPersist) => {
        const baseRequest = defaultRequestDetail?.requestId ? defaultRequestDetail : requestDetail
        if (!baseRequest?.requestId || !Array.isArray(locationsToPersist) || locationsToPersist.length === 0) return
        const saveResponse = await submitRequestForm({ ...baseRequest, sharedStoreLocations: locationsToPersist })
        if (saveResponse.status !== 200) {
            message.warning(t('Store locations shared, but could not be saved to the request.'))
            return
        }
        if (typeof getRequestDetail === "function") {
            await getRequestDetail()
        }
    }
    // Master table configured on the Google Places node, updated when sharing.
    const googlePlacesNode = (defaultRequestDetail?.originalWorkflow?.nodes || requestDetail?.originalWorkflow?.nodes || workflowData?.node || [])
        .find(node => node?.data?.type === "googlePlaces")
    const storeMasterId = googlePlacesNode?.data?.googlePlacesInfo?.masterToBeUpdated || ""

    useEffect(() => {
        if (((requestId === "new" && workflowData?.workflowId) || requestDetail.status === "draft" || defaultRequestDetail?.workflow?.length === 0) && String(requestDetail?.currentCycle) === String(defaultRequestDetail?.currentCycle)) {
            let defaultNode = workflowData?.node?.find(node => node.type === "default")
            if (defaultNode) {
                setWorkflow([{ ...defaultNode, isRequestor: true }])
            }
        } else {
            if (requestDetail.requestId && requestDetail.workflow) {
                let reqFlow = requestDetail.workflow?.map((workflow, index) => {
                    let reqNode = workflowData?.node?.find(step => {
                        return step?.data?.name?.toLowerCase() === workflow.name.toLowerCase()
                    })
                    if (reqNode) {
                        return { ...reqNode, data: { ...reqNode.data, ...workflow }, isRequestor: index === 0 ? true : false }

                    } else {
                        return { ...workflow }
                    }
                })
                setWorkflow(reqFlow)
            }
        }
    }, [workflowData, requestDetail])

    return (
        <div className="request-workflow">
            {
                workflow.map((data, index) => {
                    return <WorkflowCard {...data}
                    workflow={workflow}
                    index={index}
                    requestDetail={requestDetail}
                    defaultRequestDetail={defaultRequestDetail}
                    createdOn={requestDetail.createdOn}
                    currentValues={currentValues}
                    definedWidgets={spaceSpecificUserPickers}
                    spaceSpecificUsers={spaceSpecificUsers}
                    spaceSpecificGroups={spaceSpecificGroups}
                    approvedStepLabel={approvedStatusLabel}
                    />
                })
            }
            <GooglePlacesResultsCard locations={newLocations} requestId={activeRequestId} masterId={storeMasterId} onPersistLocations={persistLocationsOnRequest} updatedInMaster={updatedInMaster} onRefreshRequest={getRequestDetail} />
        </div>
    )
}

export default Workflow
